import DimensionUtils from "../common/DimensionUtils";
import { defaultUnitName, resolveUnitConfig, unitNamesOf } from "../common/inputs/unitConfig";
import { budgetMax, TBudgetRef } from "./paramBounds";
import type { TFieldSpec } from "./paramDefaults";
import TParameters from "./TParameters";
import ICountConfigTypeDescriptor from "../database/interfaces/paramScript/ICountConfigTypeDescriptor";
import {
    IDistributionParamTypeDescriptor,
    IRelationFieldOverride,
    TCompositeFieldDescriptor,
    TParamFieldRef,
} from "../database/interfaces/paramScript/IParamTypeDescriptor";

/**
 * TÖBB AL-MEZŐS PARAMÉTEREK — EGY fogalom, KÉT forrással.
 *
 * Egy paraméter értéke állhat több al-értékből. Eddig ezt két külön modul írta le, mert két külön
 * igényből született:
 *   - `compositeFields.ts` — NEVESÍTETT mezők fix listája (`fields`), pl. a döntött szekrény öt mezője,
 *   - `distribution.ts`    — DARABSZÁM-vezérelt ismétlés (`countFrom`), pl. zónánkénti magasságok.
 * A kettő rendering- és emit-rétege szinte azonos volt. Itt egyetlen modell mögé kerülnek: a
 * `resolveItems` a descriptor ALAKJÁBÓL felismeri a forrást, a többi függvény pedig a MODELLEL dolgozik,
 * nem a nyers descriptorral — így egy új eset (pl. a `DrillSeries` `DrillCount`-ja) csak adat.
 *
 * A modul TISZTA FÜGGVÉNYEKBŐL áll (nincs benne React): ugyanezt hívja a kártya, a betöltési guard és a
 * Unity-kimenet, hogy a látszó mezők, a kosár-descriptor és a stylescript SOSE mondhasson mást.
 *
 * KÉT FORRÁS, KÉT SAJÁT SZABÁLY — ezek szándékosan nem olvadnak össze:
 *   `fields` : testvér-mező láthatóság (`visibleWhen`), és a REJTETT mező a deklarált alapértékén megy ki
 *   `count`  : LEGALÁBB EGY arányos (`*`) mezőnek maradnia kell — ha mind fix (mm) lenne, az összegük
 *              felülírná a szekrény magasságát (600 mm-es szekrényben három 300 mm-es zóna 900-at kérne)
 *
 * IMPORT-MEGKÖTÉS: a `TFieldSpec` KIZÁRÓLAG `import type`-pal jöhet be. A `paramDefaults` → `paramGuards`
 * → `paramItems` lánc miatt egy értékszintű import kört zárna.
 */

// =====================================================================
// Modell
// =====================================================================

/** Egy levél leírója: a háromféle beviteli elem, amit egy al-mező felvehet. */
export type TItemLeaf =
    | { kind: "size"; units: any; default?: string; minFrom?: TParamFieldRef; maxFrom?: TParamFieldRef }
    | { kind: "enum"; options: { [name: string]: string } | undefined; default?: string }
    | { kind: "bool"; default?: boolean }
    | { kind: "number"; config?: ICountConfigTypeDescriptor };

/** A "be/ki" kapcsoló, ha a forrás deklarál ilyet (ma csak a `count`: "Ajánlott elosztás"). */
export type TItemsToggle = { key: string; label?: string; tooltip?: string; default: boolean };

export type TItemsModel =
    | {
        source: "fields";
        fields: TCompositeFieldDescriptor[];
        /** Kimeneti átkulcsolás (mező-kulcs → kimeneti kulcsok); lásd `itemsEmitKeyMap`. */
        emitAs?: Record<string, string[]>;
    }
    | {
        source: "count";
        /** A `plus` a darabszámhoz adott eltolás — lásd `itemCount` (n polc → n+1 polcos zóna). */
        countFrom: TParamFieldRef & { plus?: number };
        /** Az érték-objektumban a lista kulcsa. */
        listKey: string;
        itemLabel?: string;
        maxItems?: number;
        item: TItemLeaf;
        toggle: TItemsToggle;
        emitEntries?: { path: string; key: string; step?: number; start?: number };
        emitAs?: Record<string, string[]>;
        /** Az elemek együtt ebből a méretből fogyasztanak (lásd paramBounds `budgetMax`). */
        budget?: TBudgetRef;
    };

/**
 * A descriptor ALAKJÁBÓL felismert modell, vagy `null`, ha nem több al-mezős paraméter.
 *
 * AZ ÁGAK SORRENDJE SZÁMÍT, és a mai `PropertyContainerCard` sorrendjét tartja: a darabszám-vezérelt
 * ág ERŐSEBB a nevesített mezőknél. Egy descriptor elvben mindkettőt hordozhatná; a sorrend
 * megfordítása némán kártyát váltana rajta.
 *
 * A két LEGACY ág a DB mai alakját fogadja (25 `countFrom`-os és 5 `fields`-es sor) — migráció nélkül.
 * A `count` felismerés SZÁNDÉKOSAN mindhárom kulcsot megköveteli, pontosan úgy, ahogy a korábbi
 * `isDistributionDescriptor`: lazábban egy sima méret-descriptor is ide esne, és `emitEntries` híján
 * NÉMÁN kimaradna a Unity-kimenetből.
 */
export const resolveItems = (descriptor: any): TItemsModel | null => {
    const items = descriptor?.items;

    if (items?.source === "count" && items.countFrom?.paramName && items.countFrom?.property && items.item) {
        return {
            source: "count",
            countFrom: items.countFrom,
            listKey: items.listKey ?? "Heights",
            itemLabel: items.itemLabel,
            maxItems: items.maxItems,
            item: items.item,
            toggle: items.toggle ?? { key: "Auto", label: descriptor.label, default: true },
            emitEntries: items.emitEntries,
            emitAs: descriptor.emitAs,
            budget: items.budget ?? descriptor.budget,
        };
    }
    if (items?.source === "fields" && Array.isArray(items.fields)) {
        return { source: "fields", fields: items.fields, emitAs: descriptor.emitAs };
    }

    // LEGACY: a mai `countFrom` alak (distribution.ts `isDistributionDescriptor`).
    if (descriptor?.countFrom?.paramName && descriptor?.countFrom?.property && descriptor?.units) {
        const d = descriptor as IDistributionParamTypeDescriptor;
        return {
            source: "count",
            countFrom: d.countFrom,
            listKey: "Heights",
            itemLabel: d.itemLabel,
            maxItems: d.maxItems,
            item: { kind: "size", units: d.units },
            toggle: { key: "Auto", label: d.label, tooltip: d.tooltip, default: d.auto ?? true },
            emitEntries: d.emitEntries,
            emitAs: (descriptor as any).emitAs,
            budget: (descriptor as any).budget,
        };
    }

    // LEGACY: a mai `fields` alak (compositeFields.ts `isCompositeDescriptor`).
    if (Array.isArray(descriptor?.fields)) {
        return { source: "fields", fields: descriptor.fields, emitAs: descriptor.emitAs };
    }

    return null;
};

// =====================================================================
// `fields` forrás
// =====================================================================

/** A modellben felsorolt mezők (a `count` forrásnál üres lista). */
export const itemsOf = (model: TItemsModel | null): TCompositeFieldDescriptor[] =>
    model?.source === "fields" ? model.fields : [];

/**
 * Egy al-mező JELENLEGI értéke — mindig a TÁROLÁSI alakban (string), ahogy a Unity is kapja.
 * A fallback-lánc: eltárolt érték → a mező deklarált `default`-ja → strukturális végállomás.
 *
 * A "strukturális végállomás" szándékosan nem kitalált üzleti érték:
 *   - méret     → üres string; a `resolveUnitConfig` ilyenkor a `units` blokk saját defaultjára esik,
 *   - választós → az ELSŐ felsorolt opció (a felsorolás sorrendje is adat),
 *   - logikai   → "false" (a "nincs megadva" természetes olvasata a kikapcsolt állapot).
 */
export const itemValue = (field: TCompositeFieldDescriptor, value: any): string => {
    const stored = (value && typeof value === "object") ? value[field.key] : undefined;

    if (field.kind === "bool") {
        return String(stored ?? field.default ?? false) === "true" ? "true" : "false";
    }
    if (field.kind === "enum") {
        return String(stored ?? field.default ?? Object.keys(field.options ?? {})[0] ?? "");
    }
    // A `number` ágnak EXPLICITNEK kell lennie a méret-fallback ELŐTT — különben egy darabszám-mező
    // némán egység nélküli méret-mezővé válna.
    if (field.kind === "number") {
        return String(stored ?? field.config?.default ?? "");
    }
    return String(stored ?? field.default ?? "");
};

/**
 * Látszik-e az al-mező: a `visibleWhen` egy TESTVÉR mezőre hivatkozik, és akkor mutatja, ha annak az
 * értéke az `in` listában van. Feltétel nélkül a mező mindig látszik.
 *
 * Ismeretlen hivatkozásnál ENGEDÉKENY (a mező látszik) — egy elgépelt descriptor-kulcs ne tüntessen el
 * csendben egy mezőt a konfigurátorból.
 */
export const isItemVisible = (
    field: TCompositeFieldDescriptor,
    fields: TCompositeFieldDescriptor[],
    value: any
): boolean => {
    const condition = field.visibleWhen;
    if (!condition) return true;

    const source = fields.find((candidate) => candidate.key === condition.field);
    if (!source) return true;

    return condition.in.includes(itemValue(source, value));
};

/**
 * A mezőre AKTUÁLISAN érvényes numerikus felülírás, ha a descriptor `boundsBy`-t deklarál: a hivatkozott
 * TESTVÉR mező mostani értéke választja ki az ágat. Ismeretlen hivatkozásnál vagy ág nélkül `undefined`
 * — ilyenkor a mező a saját `units` configjának korlátaival dolgozik.
 *
 * A visszaadott alak ugyanaz az `IRelationFieldOverride`, amit az `UnitField` `override` propja és a
 * `guardDimension` MÁR MA IS kezel (a BackPassing `byRelation`-je óta) — nincs új fogalom, csak a
 * feloldás hiányzott a composite mezőkről.
 */
export const itemOverride = (
    field: TCompositeFieldDescriptor,
    fields: TCompositeFieldDescriptor[],
    value: any
): IRelationFieldOverride | undefined => {
    if (field.kind !== "size" || !field.boundsBy) return undefined;

    const source = fields.find((candidate) => candidate.key === field.boundsBy!.field);
    return source ? field.boundsBy.cases[itemValue(source, value)] : undefined;
};

/**
 * Egy al-mező KIADANDÓ értéke: ha a mező az adott állapotban NEM látszik, a DEKLARÁLT alapértéke.
 *
 * A rejtett mező olyan beállítás, amit a felhasználó ebben az állapotban nem is dönthet el — ilyenkor a
 * descriptor alapértéke az érvényes szabály, nem egy MÁSIK állapotban beírt, azóta értelmét vesztett
 * érték. A "deklarált alapérték" nem külön ág: ugyanaz az `itemValue`, csak eltárolt érték NÉLKÜL hívva.
 */
export const itemEmitValue = (
    field: TCompositeFieldDescriptor,
    fields: TCompositeFieldDescriptor[],
    value: any
): string =>
    isItemVisible(field, fields, value) ? itemValue(field, value) : itemValue(field, undefined);

/**
 * A mező leírója az alapérték-feloldáshoz (`paramDefaults.fieldDefault`). A háromféle levél pontosan a
 * `TFieldSpec` ágaira képződik le — nincs külön alapérték-logika az al-mezőkre.
 */
export const itemFieldSpec = (
    field: TCompositeFieldDescriptor,
    override?: IRelationFieldOverride
): TFieldSpec => {
    if (field.kind === "bool") return { bool: { default: field.default } };
    if (field.kind === "enum") return { options: field.options, default: field.default };
    if (field.kind === "number") return { number: field.config };
    // Az override a RESET-gombra is hat: az alapérték se eshessen az ághoz tartozó korlátokon kívülre.
    return { units: field.units, override };
};

// =====================================================================
// `count` forrás
// =====================================================================

/** A "be/ki" kapcsoló állapota. Hiányzó értéknél a deklarált alapérték. */
export const isToggleOn = (model: TItemsModel | null, value: any): boolean => {
    if (model?.source !== "count") return false;
    const stored = (value && typeof value === "object") ? value[model.toggle.key] : undefined;
    return String(stored ?? model.toggle.default) === "true";
};

/**
 * A darabszám feloldása a hivatkozott paraméterből. A keresés ugyanaz, mint a `visibleWhen`-nél
 * (`parameters[paramName][property]`), azzal a kiegészítéssel, hogy a ParametersProcessor a több
 * példányban szereplő paramétereket `Név_1`, `Név_2`… néven tárolja — ezért bázisnévre is illesztünk.
 *
 * Nem feloldható / értelmetlen darabszám → 0: ilyenkor egyetlen mező sem rajzolódik ki (nem találunk ki
 * elemszámot).
 *
 * A `countFrom.plus` a hivatkozott darabszámhoz ADOTT ELTOLÁS: a POLCOS zónák száma eggyel több a
 * polcokénál (n polc n+1 rekeszre osztja a szekrényt), a fiókos zónáké viszont pontosan a fiókoké.
 * Az eltolás a KLAMPOLÁS ELŐTT érvényesül, hogy a `maxItems` a tényleges mezőszámra szóljon.
 */
export const itemCount = (parameters: TParameters, model: TItemsModel | null): number => {
    if (model?.source !== "count") return 0;

    const ref = model.countFrom;
    const baseName = (name: string) => name.replace(/_\d+$/, "");
    const name = parameters?.[ref.paramName] !== undefined
        ? ref.paramName
        : Object.keys(parameters || {}).find((candidate) => baseName(candidate) === ref.paramName);

    const raw = name ? parameters?.[name]?.[ref.property] : undefined;
    const referenced = parseInt(String(raw ?? ""), 10);
    if (isNaN(referenced) || referenced < 0) return 0;

    const count = Math.max(referenced + (ref.plus ?? 0), 0);
    return Math.min(count, model.maxItems ?? count);
};

/** Az ismételt levél `units` blokkja (csak `size` levélnél értelmes). */
const leafUnits = (model: TItemsModel): any =>
    model.source === "count" && model.item.kind === "size" ? model.item.units : undefined;

/**
 * A SÚLY-egység: EGYEDÜL ez szívja fel a maradékot (`*`). Minden más egység FIX fogyasztás — a `mm`
 * abszolút méretben, a `%` a keret ismert hányadában, de egyik sem tud "azt kapni, ami marad".
 * Ez egyben a mezők fallback egysége is, ezért a közös feloldót használjuk (lásd `defaultUnitName`).
 */
const weightUnit = defaultUnitName;

/** Egy elem alapértéke: a súly-egység (`*`) a saját defaultjával — a feloldás a `resolveUnitConfig`-ban. */
const defaultItem = (model: TItemsModel): string => {
    const cfg = resolveUnitConfig(leafUnits(model), "");
    return DimensionUtils.formatDimension(cfg.num, cfg.unit);
};

/**
 * Az eltárolt lista a DARABSZÁMRA hozva: a hiányzó elemek az alapértékkel egészülnek ki, a fölöslegesek
 * lemaradnak. Így a UI és a tárolt érték nem tud elcsúszni akkor sem, ha a darabszám közben változott.
 */
export const itemList = (model: TItemsModel | null, value: any, count: number): string[] => {
    if (model?.source !== "count") return [];

    const stored: any[] = Array.isArray(value?.[model.listKey]) ? value[model.listKey] : [];
    const fallback = defaultItem(model);

    return Array.from({ length: count }, (_, index) => {
        const item = String(stored[index] ?? "");
        // A meglévő elemeket NEM formázzuk át — az egységüket a felhasználó választotta.
        return item === "" ? fallback : item;
    });
};

/**
 * Az az INDEX, aminek az egység-választóját el kell venni — vagy `-1`, ha nincs ilyen.
 *
 * Akkor és csak akkor zárolunk, ha PONTOSAN EGY arányos (a descriptor első egysége) mező maradt: azt már
 * nem lehet fixre váltani, különben egyetlen arányos sem maradna. Nem feltétlenül a fizikailag utolsó
 * mező — ha az 1.-et és a 3.-at állítottuk fixre, a 2. zárol.
 */
export const lockedItemIndex = (model: TItemsModel | null, items: string[]): number => {
    if (model?.source !== "count") return -1;

    // Zárolt az a mező, amelyik EGYEDÜLI súly-egységűként maradt: a többi (`mm` vagy `%`) már fix
    // fogyasztás, tehát a keretből visszamaradó részt kizárólag ő töltheti ki.
    const weight = weightUnit(leafUnits(model));
    if (!weight) return -1;

    const indexes = items
        .map((item, index) => ({ index, unit: DimensionUtils.parseDimension(item).unit }))
        .filter((entry) => entry.unit === weight)
        .map((entry) => entry.index);

    return indexes.length === 1 ? indexes[0] : -1;
};

/**
 * Egy elem egység-készlete. A zárolt elem CSAK az arányos egységet kapja — a NumericInput egyetlen
 * egységnél felirattá váltja a választót, tehát a szabály betartatásához nem kell új komponens-prop.
 */
export const itemUnitOptions = (model: TItemsModel | null, items: string[], index: number): string[] => {
    if (model?.source !== "count") return [];

    const units = leafUnits(model);
    if (index !== lockedItemIndex(model, items)) return unitNamesOf(units);

    // A ZÁROLT mező CSAKIS súly-egységű (`*`) lehet — ő szívja fel a maradékot. A `%` itt nem jó: az a
    // keret ismert hányadát kéri, tehát ha az utolsó szabad mező is `%` lenne, senki nem töltené ki a
    // maradékot, és az összeg nem adná ki a keretet.
    const weight = weightUnit(units);
    return weight ? [weight] : unitNamesOf(units);
};

/**
 * A lista LEGALÁBB EGY arányos elemmel: ha minden elem fix egységben áll (pl. egy régebben mentett érték,
 * vagy a darabszám csökkenése után), az UTOLSÓ elem arányosra vált. A kártya csak megakadályozza, hogy
 * előálljon; a guard viszont javítja is.
 */
export const withProportionalTail = (model: TItemsModel | null, items: string[]): string[] => {
    if (model?.source !== "count") return items;

    const units = leafUnits(model);
    const weight = weightUnit(units);
    if (!weight || !items.length) return items;

    const toWeight = (index: number) => {
        const fallback = units?.[weight]?.default ?? resolveUnitConfig(units, "").num;
        return items.map((item, i) => (i === index ? DimensionUtils.formatDimension(fallback, weight) : item));
    };

    // Ha egyetlen súly-egységű mező sem maradt (mind `mm`/`%`), az UTOLSÓ vált vissza `*`-ra: különben a
    // fix értékek összege határozná meg a befoglaló méretet, nem a szekrény magassága.
    if (!items.some((item) => DimensionUtils.parseDimension(item).unit === weight)) {
        return toWeight(items.length - 1);
    }

    return items;
};

/**
 * A KIADANDÓ lista. Bekapcsolt kapcsolónál EGYENLETES elosztás (minden elem az arányos egység
 * alapértékén) — a felhasználó ilyenkor nem is látja a mezőket, tehát a kimenet nem függhet attól, mit
 * írt be korábban. Kikapcsolva a (darabszámra hozott, arányos-farokkal védett) saját listája.
 *
 * A kézzel beírt elosztás közben ÉRINTETLENÜL megmarad az értékben, így a kapcsoló vissza-oda mozgatása
 * nem veszít adatot.
 */
export const itemOutput = (model: TItemsModel | null, value: any, count: number): string[] => {
    if (model?.source !== "count") return [];

    const items = itemList(model, value, count);
    if (!isToggleOn(model, value)) return withProportionalTail(model, items);

    const even = defaultItem(model);
    return items.map(() => even);
};

/**
 * Indexel-e az útvonal FÜGGŐLEGES ZÓNÁT (`*.DrawerZone[{i}]`, `*.ShelfZone[{i}]`)?
 *
 * Az ALAKJÁBÓL dönthető el, ezért nem kell hozzá descriptor-kulcs: ahol zónát indexelünk, ott van
 * fent és lent, és a két számozás iránya ellentétes. A tömbvágás lapjainál (`[{i}]`) nincs ilyen
 * irány — azok egy szabásterv elemei —, ezért ott nem fordítunk.
 */
/**
 * A ZÓNA-INDEXEK, a mezők sorrendjében — előre kigyűjtve, hogy a leképezés EGY helyen, olvasható
 * alakban álljon, és a kimenetet előállító hívó már csak sorra vehesse őket.
 *
 * A leképezés AZONOSSÁG: az n-edik mező az n-edik zónába megy. Egyetlen dolog miatt nem elég maga a
 * sorszám: a hierarchiában nem minden index fiók/polc — a POLCOKNÁL minden második egy Space, ezért
 * ott a lépésköz 2 (0, 2, 4 …), a fiókoknál 1 (0, 1, 2 …). Ezt a `step` mondja meg, és az ADAT.
 *
 * IRÁNYT ITT NEM FORDÍTUNK. A "melyik mező melyik fizikai polc" kérdés MEGJELENÍTÉSI kérdés, és a
 * kártyán dől el: az `ItemsCardContent` fordított sorrendben rendereli a sorokat (fentről lefelé
 * csökkenő sorszám), így a lista utolsó, "1." sorszámú eleme kerül legalulra. A számozás és a
 * kiadott érték változatlan — a kimenet tehát nem függ a megjelenítéstől.
 */
export const zoneIndices = (
    template: { step?: number; start?: number },
    count: number
): number[] => {
    const step = template.step ?? 1;
    const start = template.start ?? 0;
    return Array.from({ length: count }, (_, i) => start + i * step);
};

/**
 * A Unity-KIMENET a `count` forrásnál: elemenként EGY entry, mindegyik a saját útvonalával. A Unity nem
 * tömböt vár, hanem külön blokkokat:
 *     {"Path": "*.DrawerZone[0]", "Height": "200mm"}
 *
 * A ZÓNA-INDEX nem azonos a sorszámmal: a `step` mondja meg a lépésközt, mert a hierarchia zóna-fajtánként
 * másképp épül (fiókoknál 0,1,2 — polcoknál 0,2,4, mert minden második index egy Space) — lásd `zoneIndices`.
 *
 * `emitEntries` nélkül üres lista: a paraméter nem megy ki a Unitynak (a kártya és a kosár-descriptor
 * változatlanul él) — így egy zóna-fajta akkor is bekötheto, ha a Unity-oldali alakja még nincs eldöntve.
 */
export const itemEntries = (
    model: TItemsModel | null,
    value: any,
    count: number
): Array<Record<string, string>> => {
    if (model?.source !== "count") return [];

    const template = model.emitEntries;
    if (!template?.path || !template?.key) return [];

    const items = itemOutput(model, value, count);
    const zones = zoneIndices(template, items.length);

    return items.map((height, index) => ({
        Path: template.path.replace("{i}", String(zones[index])),
        [template.key]: height,
    }));
};

// =====================================================================
// Közös: kimeneti átkulcsolás
// =====================================================================

/**
 * A KIMENETI ÁTKULCSOLÁSOK: mező-kulcs → azok a kulcsok, amelyeken a Unity-stylescriptbe kerül. Csak
 * azok a mezők szerepelnek benne, amelyek `emitAs`-t deklarálnak; a többi a saját kulcsán megy ki.
 *
 * Két esetet fed le, EGY fogalommal:
 *   - `[]`        → a mező nem megy ki (a fióksín típusa: a TÖBBI mezőt vezérli, de a 3D-modellnek
 *                   nincs jelentése),
 *   - több kulcs  → ugyanaz az érték több kulcson megy ki (az "oldalsó ráhagyás" EGY mező a UI-n, a
 *                   Unity viszont `LeftMargin`-t és `RightMargin`-t vár).
 *
 * MINDEN paraméterre lefut (Size-ra, Decorra is), ezért nem-items descriptornál üres mapet kell adnia.
 * A descriptor-SZINTŰ `emitAs` merge-e kötelező: enélkül az elosztás `Auto: []` átkulcsolása eltűnne.
 */
export const itemsEmitKeyMap = (descriptor: any): Record<string, string[]> => {
    const map: Record<string, string[]> = { ...(descriptor?.emitAs ?? {}) };
    itemsOf(resolveItems(descriptor)).forEach((field) => {
        if (field.emitAs) map[field.key] = field.emitAs;
    });
    return map;
};

// =====================================================================
// Kártya-vetület
// =====================================================================

/** Egy megjelenítendő al-mező, forrás-függetlenül. */
export type TItemSlot = {
    /** STABIL React key — nem függhet az értéktől, különben gépelés közben elveszne a fókusz. */
    id: string;
    label: string;
    tooltip?: string;
    leaf: TItemLeaf;
    /** Tárolási alakú érték. */
    value: string;
    /** Szűkített egység-készlet, ha a forrás szabálya megköveteli. */
    unitOptions?: string[];
    /**
     * Egységváltás-szemantika. A KÉT FORRÁS MA MÁST CSINÁL, és ezt szándékosan megtartjuk: a nevesített
     * mezők megtartják a számot, a darabszám-vezérelt elemek az új egység descriptor-defaultjára
     * ugranak. Az egységesítés külön feladat — addig a modell dönt, nem a kártya.
     */
    unitChange: "keep" | "default";
    /** Az alapérték-feloldáshoz (`paramDefaults.fieldDefault`) — a kártya csak továbbadja. */
    spec: TFieldSpec;
    /** Ágfüggő numerikus felülírás (`boundsBy`), ha a mező deklarál ilyet. */
    override?: IRelationFieldOverride;
    /** A mező globális alapértéke a "visszaállítás" gombhoz; `undefined`, ha nincs. */
    storedDefault?: any;
    /** Az elem cseréje: a TELJES új paraméter-érték. */
    emit: (value: string) => any;
};

/**
 * A megjelenítendő mezők — a kártya EGYETLEN bemenete. A két forrás különbsége (nevesített kulcs vs.
 * index, statikus lista vs. darabszám) itt záródik le; a kártya már csak slotokat renderel.
 */
export const itemSlots = (
    model: TItemsModel | null,
    value: any,
    parameters: TParameters,
    storedDefault?: any
): TItemSlot[] => {
    if (!model) return [];

    if (model.source === "fields") {
        const fields = model.fields;
        return fields
            .filter((field) => isItemVisible(field, fields, value))
            .map((field) => ({
                id: field.key,
                label: field.label ?? field.key,
                tooltip: field.tooltip,
                leaf: itemLeafOf(field),
                value: itemValue(field, value),
                unitChange: "keep",
                spec: itemFieldSpec(field, itemOverride(field, fields, value)),
                override: itemOverride(field, fields, value),
                storedDefault: storedDefault?.[field.key],
                // A TELJES objektum kiadása: az ÚJ állapot ELŐSZÖR áll össze, és a mezőnkénti feloldás
                // MÁR ezen fut — mert az éppen szerkesztett mező egy TESTVÉRT elrejthet, aminek ilyenkor
                // a deklarált alapértéke a helyes kimenet.
                emit: (next: string) => {
                    const edited = { ...(value && typeof value === "object" ? value : {}), [field.key]: next };
                    const out: Record<string, string> = {};
                    fields.forEach((candidate) => {
                        out[candidate.key] = itemEmitValue(candidate, fields, edited);
                    });
                    return out;
                },
            }));
    }

    const count = itemCount(parameters, model);
    const items = itemList(model, value, count);

    return items.map((item, index) => ({
        id: `i:${index}`,
        label: model.itemLabel?.replace("{n}", String(index + 1)) ?? "",
        leaf: model.item,
        value: item,
        unitOptions: itemUnitOptions(model, items, index),
        unitChange: "default",
        spec: { units: leafUnits(model) },
        // KERET: az elem felső határa a zóna méretéből, a TÖBBI elem fogyasztása után. A meglévő
        // `override` csatornán érkezik, tehát a UnitField-hez nem kell hozzányúlni.
        override: budgetOverride(model, parameters, items, index),
        // A `count` forrás elemeinek NINCS külön eltárolt alapértéke (a globális alapérték a LISTÁÉ),
        // ezért a reset-gomb a descriptor alapértékére állít vissza.
        storedDefault: undefined,
        emit: (next: string) => emitItems(model, value, items.map(
            (current, i) => (i === index ? next : current)
        )),
    }));
};

/** Az elem felső határa a keretből — `undefined`, ha nincs keret vagy a mező súly-egységben áll. */
const budgetOverride = (
    model: TItemsModel,
    parameters: TParameters,
    items: string[],
    index: number
) => {
    if (model.source !== "count") return undefined;

    const unit = DimensionUtils.parseDimension(items[index]).unit;
    const others = items.filter((_, i) => i !== index);
    const max = budgetMax(parameters, model.budget, unit, others);

    return max === undefined ? undefined : { max };
};

/** Egy `fields`-mező leíróból levél-leíró (a `kind` ágai egy az egyben). */
const itemLeafOf = (field: TCompositeFieldDescriptor): TItemLeaf => {
    if (field.kind === "bool") return { kind: "bool", default: field.default };
    if (field.kind === "enum") return { kind: "enum", options: field.options, default: field.default };
    if (field.kind === "number") return { kind: "number", config: field.config };
    return { kind: "size", units: field.units, default: field.default,
             minFrom: field.minFrom, maxFrom: field.maxFrom };
};

/** A `count` forrás teljes értéke egy új listával — a kapcsoló állapotát megtartva. */
const emitItems = (model: TItemsModel, value: any, items: string[]): any => {
    if (model.source !== "count") return value;
    return {
        [model.toggle.key]: String(isToggleOn(model, value)),
        [model.listKey]: withProportionalTail(model, items),
    };
};

/** A kapcsoló leírója a kártyának, vagy `null`, ha a forrás nem deklarál ilyet. */
export const itemsToggle = (
    model: TItemsModel | null,
    value: any
): { label?: string; tooltip?: string; checked: boolean; emit: (checked: boolean) => any } | null => {
    if (model?.source !== "count") return null;

    return {
        label: model.toggle.label,
        tooltip: model.toggle.tooltip,
        checked: isToggleOn(model, value),
        emit: (checked: boolean) => ({
            [model.toggle.key]: String(checked),
            [model.listKey]: withProportionalTail(
                model,
                Array.isArray(value?.[model.listKey]) ? value[model.listKey] : []
            ),
        }),
    };
};
